import { useEffect, useState } from 'react'
import type { VisitResponse } from '@/api/types'
import { Banner } from '@/components/Banner'
import { Icon } from '@/components/Icon'
import { LinkButton } from '@/components/LinkButton'
import { AttachmentStrip } from '@/features/attachments/AttachmentStrip'
import { cx } from '@/lib/cx'
import { formatVisitDate } from '@/lib/dates'

interface Props {
  visit: VisitResponse
  /** Controlled by the list, so opening one closes the last: a history of forty
   *  consultations all open at once is a page nobody can find anything in. The
   *  most recent starts open — find patient → identity → last consultation. */
  expanded: boolean
  onToggle: () => void
}

const ANIMATION_MS = 200

export function VisitCard({ visit, expanded, onToggle }: Props) {
  const voided = visit.status === 'voided'

  // The body is mounted only while it is open or closing. Keeping every visit's
  // body in the DOM would ask the server for a thumbnail for every attachment
  // on every consultation, which on clinic wifi is the whole page.
  const [mounted, setMounted] = useState(expanded)
  const [shown, setShown] = useState(expanded)

  useEffect(() => {
    if (expanded) {
      setMounted(true)
      // Two frames: one to get the closed state painted, one to leave it.
      // Flipping both in a single frame paints neither and nothing animates.
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    setShown(false)
    const timer = setTimeout(() => setMounted(false), ANIMATION_MS)
    return () => clearTimeout(timer)
  }, [expanded])

  return (
    // Rows in one list rather than separate floating cards: separators cost
    // less vertical space than four borders each, so more history fits.
    <li
      className={cx(
        'not-first:border-t not-first:border-line-soft',
        expanded ? 'bg-subtle' : 'bg-surface',
        voided && 'opacity-80',
      )}
    >
      <button
        type="button"
        className="flex min-h-row w-full items-center gap-3 bg-transparent px-5 py-2 text-left text-fg transition-colors duration-150 ease-standard hover:bg-hover"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="min-w-30 font-semibold whitespace-nowrap tabular-nums">{formatVisitDate(visit.visit_date)}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">
          {visit.clinical_redacted
            ? 'Clinical details not visible to your role'
            : (visit.note_excerpt ?? 'No notes recorded')}
        </span>
        <span className="flex items-center gap-3 text-fg-subtle">
          {/* Each icon is backed by text for screen readers below: never a glyph
              or a colour alone. */}
          {visit.has_test_results && <Icon name="file" size="1rem" />}
          {visit.has_prescription && <Icon name="pill" size="1rem" />}
          {visit.has_attachments && <Icon name="paperclip" size="1rem" />}
          <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size="1rem" />
        </span>
        <span className="sr-only">
          {visit.has_test_results ? 'Has test results. ' : ''}
          {visit.has_prescription ? 'Has prescription. ' : ''}
          {visit.has_attachments ? 'Has attachments. ' : ''}
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      {mounted && (
        // `grid-rows: 0fr -> 1fr` animates to the content's own height. A
        // max-height transition needs a number picked in advance, which is
        // always wrong for a note somebody actually wrote.
        <div
          className={cx(
            'grid transition-[grid-template-rows] duration-200 ease-standard',
            shown ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
        <div className="flex flex-col gap-4 px-5 pb-5">
          {voided && (
            <Banner tone="warning">
              Marked as entered in error{visit.void_reason ? `: ${visit.void_reason}` : ''}. The
              record is kept.
            </Banner>
          )}

          {visit.clinical_redacted ? (
            <Banner tone="info">
              Notes, test results and prescriptions are not visible to your role.
            </Banner>
          ) : (
            <>
              {visit.chief_complaints && (
                <section>
                  <p className="mb-2 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">Chief complaints</p>
                  <div className="overflow-hidden rounded-md border border-line-soft bg-surface">
                    <p className="max-w-prose px-4 py-3 leading-relaxed whitespace-pre-wrap">
                      {visit.chief_complaints}
                    </p>
                  </div>
                </section>
              )}
              {visit.notes && (
                <section>
                  <p className="mb-2 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">Clinical findings</p>
                  <div className="overflow-hidden rounded-md border border-line-soft bg-surface">
                    <p className="max-w-prose px-4 py-3 leading-relaxed whitespace-pre-wrap">{visit.notes}</p>
                  </div>
                </section>
              )}
              {visit.test_results.length > 0 && (
                <section>
                  <p className="mb-2 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">Test results</p>
                  <div className="overflow-x-auto overscroll-x-contain rounded-md border border-line-soft bg-surface" tabIndex={0} role="region" aria-label="Test results">
                    <table className="w-full border-collapse text-sm [&_td]:border-t [&_td]:border-line-soft [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_th]:bg-subtle [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-2xs [&_th]:font-semibold [&_th]:tracking-wide [&_th]:whitespace-nowrap [&_th]:text-fg-muted [&_th]:uppercase">
                      <thead>
                        <tr>
                          <th scope="col">Test</th>
                          <th scope="col">Result</th>
                          <th scope="col">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visit.test_results.map((t) => (
                          <tr key={t.position}>
                            <td className="font-semibold">{t.test_name}</td>
                            <td>{t.result ?? '—'}</td>
                            <td>{t.recorded_on ? formatVisitDate(t.recorded_on) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              {visit.prescription.length > 0 && (
                <section>
                  <p className="mb-2 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">Prescription</p>
                  <div
                    // Visually distinct, so "what did I give them last time" is
                    // findable without reading the whole note.
                    className="overflow-x-auto overscroll-x-contain rounded-md border border-line-soft border-l-2 border-l-accent bg-surface"
                    tabIndex={0}
                    role="region"
                    aria-label="Prescription"
                  >
                    <table className="w-full border-collapse text-sm [&_td]:border-t [&_td]:border-line-soft [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_th]:bg-subtle [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-2xs [&_th]:font-semibold [&_th]:tracking-wide [&_th]:whitespace-nowrap [&_th]:text-fg-muted [&_th]:uppercase">
                      <thead>
                        <tr>
                          <th scope="col">Medicine</th>
                          <th scope="col">Dose</th>
                          <th scope="col">Frequency</th>
                          <th scope="col">Duration</th>
                          <th scope="col">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visit.prescription.map((item) => (
                          <tr key={`${item.position}-${item.drug_name}`}>
                            <td className="font-semibold">{item.drug_name}</td>
                            <td>{item.dose ?? '—'}</td>
                            <td>{item.frequency ?? '—'}</td>
                            <td>{item.duration ?? '—'}</td>
                            <td>{item.instructions ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}

          {visit.attachments.length > 0 && (
            <section>
              <p className="mb-2 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">Attachments</p>
              <AttachmentStrip attachments={visit.attachments} />
            </section>
          )}

          {!voided && (
            <div>
              <LinkButton to={`/visits/${visit.id}/edit`} variant="secondary">
                Edit this consultation
              </LinkButton>
            </div>
          )}
          {visit.updated_at !== visit.created_at && (
            <p className="text-xs text-fg-subtle">Edited {formatVisitDate(visit.updated_at.slice(0, 10))}</p>
          )}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
